import React, { useMemo } from 'react';
import { Box, Stack, Tooltip } from '@mui/material';
import {
  ListItemCardSubtitle,
  ListItemCardTitle,
  LinkCard,
  useApiClient,
  useFetchImmutable,
} from '@marageti/z4-lib';
import { PartnerCountry, PartnerSearchHit } from '@marageti/z4-sdk/lib/travel';
import { CountryCodeMap } from '@marageti/z4-sdk/lib/standards';
import { partnerStatusIcons } from '../status-icons';

type PartnerCardProps = {
  partner: PartnerSearchHit;
};

const getCountryNameString = (countries: PartnerCountry[], countryCodes: CountryCodeMap): string => (
  countries.map((c) => countryCodes[c.country]).filter((c) => !!c).join(', ')
);

const partnerStatusDisplayMap = {
  ONBOARDING: 'Onboarding',
  NEW: 'New',
  ACTIVE: 'Active',
  NON_STRATEGIC: 'Non Strategic',
  PHASING_OUT: 'Phasing Out',
  INACTIVE: 'Inactive',
};

const PartnerCard = ({ partner }: PartnerCardProps) => {
  const apiClient = useApiClient();
  const { data: countryCodes } = useFetchImmutable<CountryCodeMap>(apiClient.standardsClient.getCountryCodes, [], true);
  const primaryCountryNames = useMemo(() => {
    if (countryCodes) {
      return getCountryNameString(partner.primaryCountries, countryCodes);
    }
    return undefined;
  }, [countryCodes, partner.primaryCountries]);
  const secondaryCountryNames = useMemo(() => {
    if (countryCodes && partner.secondaryCountries) {
      return getCountryNameString(partner.secondaryCountries, countryCodes);
    }
    return undefined;
  }, [countryCodes, partner.secondaryCountries]);

  return (
    <LinkCard key={partner.id} to={partner.id}>
      <Stack
        direction="row"
        spacing={1}
        justifyContent="space-between"
        alignItems="center"
      >
        <Stack alignItems="space-between" minHeight="45px">
          <ListItemCardTitle>
            {partner.name}
          </ListItemCardTitle>
          <ListItemCardSubtitle>
            {primaryCountryNames}
            {secondaryCountryNames && (
              ` • ${secondaryCountryNames}`
            )}
          </ListItemCardSubtitle>
        </Stack>
        <Tooltip title={partnerStatusDisplayMap[partner.status]} placement="top">
          <Box
            onMouseOver={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
          >
            {partnerStatusIcons[partner.status].icon}
          </Box>
        </Tooltip>
      </Stack>
    </LinkCard>
  );
};

export default PartnerCard;
